import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { differenceInSeconds, format } from 'date-fns';
import type { Playlist } from '@jwp/ott-common/types/playlist';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';
import { createURL, liveChannelsURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { generateMovieJSONLD } from '@jwp/ott-common/src/utils/structuredData';
import useLiveChannels from '@jwp/ott-hooks-react/src/useLiveChannels';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import useLiveProgram from '@jwp/ott-hooks-react/src/useLiveProgram';
import Restart from '@jwp/ott-theme/assets/icons/restart.svg?react';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import ReactGA from 'react-ga4';
import _ from 'lodash';

import type { ScreenComponent } from '../../../../../types/screens';
import Epg from '../../../../components/Epg/Epg';
import ShareButton from '../../../../components/ShareButton/ShareButton';
import StartWatchingButton from '../../../../containers/StartWatchingButton/StartWatchingButton';
import Cinema from '../../../../containers/Cinema/Cinema';
import Button from '../../../../components/Button/Button';
import Tag from '../../../../components/Tag/Tag';
import Loading from '../../../Loading/Loading';
import VideoDetails from '../../../../components/VideoDetails/VideoDetails';
import Icon from '../../../../components/Icon/Icon';
import VideoMetaData from '../../../../components/VideoMetaData/VideoMetaData';
import { useTheme } from '../../../../../../../ThemeContext';

import styles from './PlaylistLiveChannels.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

const PlaylistLiveChannels: ScreenComponent<Playlist> = ({ data: { feedid, playlist }, titlePath }) => {
  const { t } = useTranslation('epg');

  // Config
  const { config } = useConfigStore(({ config }) => ({ config }), shallow);
  const { siteName } = config;
  const { universalStyles } = useTheme();

  const seenProgress = useRef<Set<number>>(new Set());
  const { isAnalyticsEnable } = useAppConfig();
  const user = useAccountStore((state) => state.user);
  const { click } = useContentStateStore.getState();
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user?.id : 'Guest user';
  const deviceType = useGADeviceType();
  const labelRef = useRef<string | undefined>();

  // Routing
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const play = searchParams.get('play') === '1';
  const channelId = searchParams.get('channel') ?? undefined;
  const liveStartDateTime = searchParams.get('start');
  const liveEndDateTime = searchParams.get('end');
  const liveFromBeginning = searchParams.get('beginning') === '1';
  const goBack = () => feedid && navigate(liveChannelsURL(feedid, channelId, false, titlePath));
  const { isEpgEnable } = useAppConfig();

  // EPG data
  const [initialChannelId] = useState(channelId);
  const { channels, channel, program, setActiveChannel } = useLiveChannels({ playlist, initialChannelId, enableAutoUpdate: !liveFromBeginning });
  const { isLive, isVod, isWatchableFromBeginning } = useLiveProgram({ program, catchupHours: channel?.catchupHours });

  // Media item
  const channelMediaItem = useMemo(() => playlist.find(({ mediaid }) => channel?.id === mediaid), [channel?.id, playlist]);
  const { isEntitled } = useEntitlement(channelMediaItem);

  const videoDetails = useMemo(() => {
    if (isLive && program) {
      return {
        title: program.title,
        description: program.description || '',
        image: program.backgroundImage,
        canWatch: isLive,
        canWatchFromBeginning: false,
      };
    } else if (program) {
      return {
        title: program.title,
        description: program.description || '',
        image: program.backgroundImage,
        canWatch: isLive,
        canWatchFromBeginning: isEntitled && isLive && isWatchableFromBeginning,
      };
    }

    return {
      title: channel?.title || '',
      description: channel?.description || '',
      image: channel?.backgroundImage,
      canWatch: true,
      canWatchFromBeginning: false,
    };
  }, [channel?.backgroundImage, channel?.description, channel?.title, isEntitled, isLive, isVod, isWatchableFromBeginning, program]);

  const primaryMetadata = useMemo(() => {
    if (!channel) {
      return '';
    }

    if (!program) {
      return <Tag isLive>{t('common:live')}</Tag>;
    }

    const startTime = new Date(program.startTime);
    const endTime = new Date(program.endTime);
    const durationInSeconds = differenceInSeconds(endTime, startTime);
    const duration = formatDurationTag(durationInSeconds);
    const attributes = [t('on_channel', { name: channel.title }), duration].filter(Boolean);

    return (
      <>
        <Tag className={styles.tag} isLive={isLive}>
          {isLive ? t('common:live') : `${format(startTime, 'p')} - ${format(endTime, 'p')}`}
        </Tag>
        <VideoMetaData attributes={attributes} style={universalStyles.secondaryText} />
      </>
    );
  }, [channel, isLive, program, t]);

  // Handlers
  const handleProgramClick = (programId: string, channelId: string) => {
    setActiveChannel(channelId, programId);

    // scroll to top when clicking a program
    (document.scrollingElement || document.body).scroll({ top: 0, behavior: 'smooth' });
  };

  const handleChannelClick = (channelId: string) => {
    setActiveChannel(channelId);
  };

  // Effects
  useEffect(() => {
    // update the channel id in URL
    if (channel && feedid && channelId !== channel.id) {
      navigate(liveChannelsURL(feedid, channel.id, false, titlePath), { replace: true });
    }
  }, [navigate, feedid, channel, channelId, titlePath]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }
    seenProgress.current.clear();
    const pathname = location.pathname;
    const segments = pathname.split('/');
    const contentIdFromPath = segments[2];
    const activeItem = config?.menu?.find((item) => item.contentId === contentIdFromPath);
    if (pathname === '/') {
      labelRef.current = 'Home';
    } else if (click?.label) {
      labelRef.current = click.label;
    } else if (activeItem?.label) {
      labelRef.current = activeItem?.label;
    }
  }, [playlist.length, location.pathname, config.menu, click]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      if (scrollHeight <= 0) return;
      const progress = (scrollTop / scrollHeight) * 100;
      const roundedProgress = Math.ceil(progress);
      [25, 50, 75, 100].forEach((threshold) => {
        if (roundedProgress >= threshold && !seenProgress.current.has(threshold)) {
          if (roundedProgress >= 25 && !seenProgress.current.has(25)) {
            seenProgress.current.add(25);
            sendGAEvent('scroll', {
              firebase_screen_class: labelRef.current,
              firebase_screen: labelRef.current,
              percent_scrolled: '25%',
              scroll_direction: 'down',
              userid_no: userId,
              device_type: deviceType,
            });
          }
          if (roundedProgress >= 50 && !seenProgress.current.has(50)) {
            seenProgress.current.add(50);
            sendGAEvent('scroll', {
              firebase_screen_class: labelRef.current,
              firebase_screen: labelRef.current,
              percent_scrolled: '50%',
              scroll_direction: 'down',
              userid_no: userId,
              device_type: deviceType,
            });
          }
          if (roundedProgress >= 75 && !seenProgress.current.has(75)) {
            seenProgress.current.add(75);
            sendGAEvent('scroll', {
              firebase_screen_class: labelRef.current,
              firebase_screen: labelRef.current,
              percent_scrolled: '75%',
              scroll_direction: 'down',
              userid_no: userId,
              device_type: deviceType,
            });
          }
          if (roundedProgress >= 100 && !seenProgress.current.has(100)) {
            seenProgress.current.add(100);
            sendGAEvent('scroll', {
              firebase_screen_class: labelRef.current,
              firebase_screen: labelRef.current,
              percent_scrolled: '100%',
              scroll_direction: 'down',
              userid_no: userId,
              device_type: deviceType,
            });
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [playlist.length]);

  useEffect(() => {
    if (!isAnalyticsEnable) {
      return;
    }

    let lastScrollTop = 0;

    const handleScroll = _.throttle(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollTop > lastScrollTop ? 'Down' : 'Up';
      lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;

      if (direction == 'Up' && scrollTop > 0) {
        sendGAEvent('scroll', {
          firebase_screen_class: labelRef.current,
          firebase_screen: labelRef.current,
          scroll_direction: 'up',
          userid_no: userId,
          device_type: deviceType,
        });
      }
    }, 1000);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [playlist.length]);

  // Loading (channel and feedid must be defined)
  if (!channel || !feedid) {
    return <Loading />;
  }

  // SEO (for channels)
  // const getUrl = (id: string) => liveChannelsURL(feedid, id);
  const canonicalUrl = `${window.location.origin}${liveChannelsURL(feedid, channel.id)}`;
  const pageTitle = `${channel.title} - ${siteName}`;

  const shareButton = channelMediaItem ? (
    <ShareButton title={channelMediaItem.title} description={channelMediaItem.description} url={window.location.href} />
  ) : null;

  // @todo: bring all props to liveChannelsURL, so that createURL isn't needed
  const startWatchingButton = channelMediaItem ? (
    <>
      <StartWatchingButton
        item={channelMediaItem}
        playUrl={createURL(liveChannelsURL(feedid, channelId, true, titlePath), {
          start: isVod ? program?.startTime : undefined,
          end: isVod ? program?.endTime : undefined,
        })}
        disabled={!videoDetails.canWatch}
        isLive={isLive}
      />
      {videoDetails.canWatchFromBeginning && (
        <Button
          color="videoDetailButton"
          variant="contained"
          size="exlarge"
          className={styles.catchupButton}
          onClick={() =>
            navigate(
              createURL(liveChannelsURL(feedid || '', channelId, true), {
                start: program?.startTime,
                beginning: 1,
              }),
            )
          }
          // label={t('start_from_beginning')}
          startIcon={<Icon icon={Restart} />}
        />
      )}
    </>
  ) : null;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content={channelMediaItem?.description} />
        <meta property="og:description" content={channelMediaItem?.description} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="video.other" />
        {channelMediaItem?.image && <meta property="og:image" content={channelMediaItem.image?.replace(/^https:/, 'http:')} />}
        {channelMediaItem?.image && <meta property="og:image:secure_url" content={channelMediaItem.image?.replace(/^http:/, 'https:')} />}
        <meta property="og:image:width" content={channelMediaItem?.image ? '720' : ''} />
        <meta property="og:image:height" content={channelMediaItem?.image ? '406' : ''} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={channelMediaItem?.description} />
        <meta name="twitter:image" content={channelMediaItem?.image} />
        <meta property="og:video" content={canonicalUrl.replace(/^https:/, 'http:')} />
        <meta property="og:video:secure_url" content={canonicalUrl.replace(/^http:/, 'https:')} />
        <meta property="og:video:type" content="text/html" />
        <meta property="og:video:width" content="1280" />
        <meta property="og:video:height" content="720" />
        {channelMediaItem?.tags?.split(',').map((tag) => (
          <meta property="og:video:tag" content={tag} key={tag} />
        ))}
        {channelMediaItem ? <script type="application/ld+json">{generateMovieJSONLD(channelMediaItem, window.location.origin)}</script> : null}
      </Helmet>
      <VideoDetails
        title={videoDetails.title}
        description={videoDetails.description}
        image={videoDetails.image}
        startWatchingButton={startWatchingButton}
        shareButton={null}
        primaryMetadata={primaryMetadata}
        item={videoDetails}
      >
        {channelMediaItem && (
          <Cinema
            open={play && isEntitled}
            onClose={goBack}
            item={channelMediaItem}
            title={videoDetails.title}
            primaryMetadata={primaryMetadata}
            feedId={feedid}
            liveStartDateTime={liveStartDateTime}
            liveEndDateTime={liveEndDateTime}
            liveFromBeginning={liveFromBeginning}
          />
        )}
        {isEpgEnable ? (
          <div className={styles.epgContainer}>
            <Epg
              channels={channels}
              onChannelClick={handleChannelClick}
              onProgramClick={handleProgramClick}
              selectedChannel={channel}
              program={program}
              config={config}
            />
          </div>
        ) : null}
      </VideoDetails>
    </>
  );
};

export default PlaylistLiveChannels;
